import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { SectionHeading, TEMPLE_COLORS } from "../shared";

const PhotoLabel = ({ children, color, m, sx }) => (
  <Box
    sx={{
      position: "absolute",
      top: m ? 10 : 20,
      px: m ? "10px" : "16px",
      py: m ? "5px" : "8px",
      borderRadius: "100px",
      background: color,
      ...sx,
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: m ? "10px" : "13px",
        lineHeight: m ? "12px" : "18px",
        letterSpacing: m ? "1.5px" : "2px",
        textTransform: "uppercase",
        color: "#FFFFFF",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const cardSx = {
  background: "#FFFFFF",
  border: "1px solid #EAEAEA",
  borderRadius: "20px",
};

// One story: before/after photos, then the story card and the quote card.
// Parts a story has no content for are left out.
const StorySlide = ({ story, isMobile, isTablet }) => {
  const hasPhotos = Boolean(story.beforeImage || story.afterImage);
  const hasStory = Boolean(
    story.storyLabel || story.storyTitle || story.storyParagraphs.length,
  );
  const hasQuoteCard = Boolean(
    story.quote || story.quoteAuthor || story.stats.length,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "16px" : "24px",
      }}
    >
      {/* Before / After */}
      {hasPhotos && (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "row",
            height: isMobile ? 200 : isTablet ? 320 : 440,
            borderRadius: isMobile ? "16px" : "20px",
            overflow: "hidden",
          }}
        >
          {[
            {
              src: story.beforeImage,
              label: "Before",
              color: TEMPLE_COLORS.ink,
              labelSx: { left: isMobile ? 10 : 20 },
            },
            {
              src: story.afterImage,
              label: "After",
              color: TEMPLE_COLORS.red,
              labelSx: { right: isMobile ? 10 : 20 },
            },
          ].map((half) => (
            <Box
              key={half.label}
              sx={{
                position: "relative",
                flex: 1,
                minWidth: 0,
                height: "100%",
                background: "#E8E8E8",
              }}
            >
              {half.src && (
                <Box
                  component="img"
                  src={half.src}
                  alt={`${half.label} restoration`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
              <PhotoLabel color={half.color} m={isMobile} sx={half.labelSx}>
                {half.label}
              </PhotoLabel>
            </Box>
          ))}
          <Box
            sx={{
              position: "absolute",
              left: isMobile ? "calc(50% - 1px)" : "calc(50% - 1.5px)",
              top: 0,
              width: isMobile ? 2 : 3,
              height: "100%",
              background: "#FFFFFF",
              boxShadow: isMobile
                ? "none"
                : "0px 0px 8px 0px rgba(0, 0, 0, 0.25)",
            }}
          />
        </Box>
      )}

      {/* Story + quote */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          gap: isMobile ? "16px" : "24px",
          alignItems: "stretch",
        }}
      >
        {hasStory && (
          <Box
            sx={{
              ...cardSx,
              borderRadius: isMobile ? "16px" : "20px",
              flex: 1,
              minWidth: 0,
              p: isMobile ? "20px" : "40px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "10px" : "14px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "11px" : "13px",
                lineHeight: isMobile ? "14px" : "16px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: TEMPLE_COLORS.red,
              }}
            >
              {story.storyLabel}
            </Typography>
            <Typography
              component="h3"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "16px" : "26px",
                lineHeight: isMobile ? "22px" : "34px",
                color: TEMPLE_COLORS.ink,
              }}
            >
              {story.storyTitle}
            </Typography>
            {story.storyParagraphs.map((paragraph) => (
              <Typography
                key={paragraph}
                sx={{
                  fontSize: isMobile ? "13px" : "16px",
                  lineHeight: isMobile ? "20px" : "27px",
                  color: TEMPLE_COLORS.muted,
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>
        )}

        {hasQuoteCard && (
          <Box
            sx={{
              ...cardSx,
              borderRadius: isMobile ? "16px" : "20px",
              width: isTablet ? "auto" : 480,
              flexShrink: 0,
              p: isMobile ? "20px" : "40px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: isMobile ? "18px" : "32px",
            }}
          >
            {(story.quote || story.quoteAuthor) && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                {story.quote && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: "italic",
                      fontSize: isMobile ? "16px" : "22px",
                      lineHeight: isMobile ? "24px" : "32px",
                      color: TEMPLE_COLORS.ink,
                    }}
                  >
                    {story.quote}
                  </Typography>
                )}
                {story.quoteAuthor && (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: isMobile ? "11px" : "14px",
                      lineHeight: isMobile ? "14px" : "20px",
                      color: TEMPLE_COLORS.muted,
                    }}
                  >
                    {story.quoteAuthor}
                  </Typography>
                )}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              {story.stats.map((stat) => (
                <Box
                  key={stat.label}
                  sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "24px" : "40px",
                      lineHeight: isMobile ? "30px" : "46px",
                      color: TEMPLE_COLORS.ink,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: isMobile ? "11px" : "14px",
                      lineHeight: isMobile ? "14px" : "20px",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      color: TEMPLE_COLORS.muted,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ArrowButton = ({ direction, disabled, onClick }) => (
  <IconButton
    onClick={onClick}
    disabled={disabled}
    aria-label={direction < 0 ? "Previous story" : "Next story"}
    sx={{
      width: 44,
      height: 44,
      background: "#FFFFFF",
      border: "1px solid #EAEAEA",
      color: TEMPLE_COLORS.ink,
      transition: "box-shadow 0.25s ease, border-color 0.25s ease, color 0.25s ease",
      "&:hover": {
        background: "#FFFFFF",
        borderColor: TEMPLE_COLORS.red,
        color: TEMPLE_COLORS.red,
        boxShadow: "0px 6px 18px 0px rgba(240, 73, 81, 0.35)",
      },
      "&.Mui-disabled": { background: "#FFFFFF", opacity: 0.4 },
    }}
  >
    {direction < 0 ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </IconButton>
);

// Several stories, one per slide: swipe on mobile, arrows + dots on desktop.
// The heading's one-line description follows the story on screen.
const RestorationStorySection = ({ content }) => {
  const isTablet = useMediaQuery("(max-width: 1099px)");
  const isMobile = useMediaQuery("(max-width: 599px)");
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [trackHeight, setTrackHeight] = useState(null);
  const stories = content.stories || [];
  const hasMany = stories.length > 1;

  // The track takes the height of the story on screen, so a short story
  // doesn't leave the gap of a longer one above the dots.
  useEffect(() => {
    const slide = slideRefs.current[active];
    if (!slide || !hasMany) return undefined;
    const update = () => setTrackHeight(slide.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(slide);
    return () => observer.disconnect();
  }, [active, hasMany, stories.length]);

  const goTo = (index) => {
    const track = trackRef.current;
    if (!track) return;
    const target = Math.max(0, Math.min(stories.length - 1, index));
    track.scrollTo({ left: target * track.clientWidth, behavior: "smooth" });
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setActive(
      Math.min(
        stories.length - 1,
        Math.round(track.scrollLeft / track.clientWidth),
      ),
    );
  };

  if (!stories.length) return null;

  return (
    <Box
      component="section"
      sx={{
        background: "#F7F7F7",
        px: isMobile ? "20px" : "40px",
        py: isMobile ? "76px" : "100px",
      }}
    >
      <SectionHeading
        tag={content.tag}
        title={content.title}
        description={stories[active]?.description}
        isMobile={isMobile}
      />

      <Box sx={{ maxWidth: 1360, mx: "auto", mt: isMobile ? "16px" : "56px" }}>
        <Box
          ref={trackRef}
          onScroll={handleScroll}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            height: hasMany && trackHeight ? `${trackHeight}px` : "auto",
            transition: "height 0.3s ease",
            overflowX: hasMany ? "auto" : "hidden",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            WebkitOverflowScrolling: "touch",
          }}
        >
          {stories.map((story, index) => (
            <Box
              key={index}
              ref={(node) => {
                slideRefs.current[index] = node;
              }}
              aria-hidden={index !== active}
              sx={{
                flex: "0 0 100%",
                minWidth: 0,
                scrollSnapAlign: "start",
                scrollSnapStop: "always",
              }}
            >
              <StorySlide
                story={story}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </Box>
          ))}
        </Box>

        {hasMany && (
          <Box
            sx={{
              mt: isMobile ? "16px" : "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {!isMobile && (
              <ArrowButton
                direction={-1}
                disabled={active === 0}
                onClick={() => goTo(active - 1)}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {stories.map((_, index) => (
                <Box
                  key={index}
                  component="button"
                  type="button"
                  aria-label={`Show story ${index + 1}`}
                  onClick={() => goTo(index)}
                  sx={{
                    p: 0,
                    border: "none",
                    cursor: "pointer",
                    width: index === active ? 20 : 6,
                    height: 6,
                    borderRadius: "3px",
                    background:
                      index === active ? TEMPLE_COLORS.red : "#D9D9D9",
                    transition: "width 0.2s ease, background-color 0.2s ease",
                  }}
                />
              ))}
            </Box>
            {!isMobile && (
              <ArrowButton
                direction={1}
                disabled={active === stories.length - 1}
                onClick={() => goTo(active + 1)}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RestorationStorySection;
